import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";

const fmtYMD = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

type Options = {
  /** Default start date if not in URL */
  defaultStartDate?: Date;
  /** Default end date if not in URL */
  defaultEndDate?: Date;
};

export const useOrdersUrlState = (opts: Options = {}) => {
  const [sp, setSp] = useSearchParams();

  /** ---------- BATCHED PATCHER (coalesces same-tick updates) ---------- */
  const pendingRef = useRef<URLSearchParams | null>(null);
  const flushIdRef = useRef<number | null>(null);

  const flushPatches = useCallback(() => {
    if (!pendingRef.current) return;
    const next = pendingRef.current;
    pendingRef.current = null;
    flushIdRef.current = null;
    setSp(next, { replace: true });
  }, [setSp]);

  const patchMany = useCallback(
    (changes: Record<string, string | null>) => {
      const base = pendingRef.current ?? new URLSearchParams(sp);

      for (const [k, v] of Object.entries(changes)) {
        if (v == null || v === "") base.delete(k);
        else base.set(k, v);
      }

      pendingRef.current = base;

      if (flushIdRef.current == null) {
        flushIdRef.current = requestAnimationFrame(flushPatches);
      }
    },
    [sp, flushPatches],
  );

  // Cancel any pending flush on unmount so navigating away doesn't mutate the next page's URL.
  useEffect(() => {
    return () => {
      if (flushIdRef.current != null) {
        cancelAnimationFrame(flushIdRef.current);
        flushIdRef.current = null;
        pendingRef.current = null;
      }
    };
  }, []);

  const patch = useCallback((kv: Record<string, string | null>) => patchMany(kv), [patchMany]);

  // freeze defaults on mount: the page opens on today's orders only
  const todayRef = useRef(new Date());
  const defaultStartRef = useRef(opts.defaultStartDate ?? new Date(todayRef.current));
  const defaultEndRef = useRef(opts.defaultEndDate ?? new Date(todayRef.current));

  // URL params (strings)
  const startS = sp.get("start_date");
  const endS = sp.get("end_date");

  // stable strings for API (ISO format)
  const startISO = useMemo(() => {
    if (startS) {
      const date = new Date(startS);
      date.setHours(0, 0, 0, 0);
      return date.toISOString();
    }
    const date = new Date(defaultStartRef.current);
    date.setHours(0, 0, 0, 0);
    return date.toISOString();
  }, [startS]);

  const endISO = useMemo(() => {
    if (endS) {
      const date = new Date(endS);
      date.setHours(23, 59, 59, 999);
      return date.toISOString();
    }
    const date = new Date(defaultEndRef.current);
    date.setHours(23, 59, 59, 999);
    return date.toISOString();
  }, [endS]);

  // UI dates for pickers
  const startDate = useMemo(() => {
    if (startS) {
      return new Date(startS);
    }
    return new Date(defaultStartRef.current);
  }, [startS]);

  const endDate = useMemo(() => {
    if (endS) {
      return new Date(endS);
    }
    return new Date(defaultEndRef.current);
  }, [endS]);

  // Handlers for date changes
  const handleDateChange = useCallback(
    (key: "startDate" | "endDate", value: Date) => {
      if (key === "startDate") {
        patch({ start_date: fmtYMD(value), page: null });
      } else {
        patch({ end_date: fmtYMD(value), page: null });
      }
    },
    [patch],
  );

  return {
    // UI dates (for date pickers)
    startDate,
    endDate,

    // API dates (ISO strings for API calls)
    startISO,
    endISO,

    // Handlers
    handleDateChange,

    // Raw URL params access
    searchParams: sp,
    patch,
  };
};
